import { ConnectivityStatus } from '@evimo/types/enums/connectivity-status';
import { isUnauthorizedError } from '~/app/services/asset/asset-list-error';
import type {
  ConnectivityBadge,
  LastSeenCell,
} from '~/app/components/asset-overview/AssetOverview.types';

const BADGES: Record<
  ConnectivityStatus,
  Pick<ConnectivityBadge, 'tone' | 'label'>
> = {
  [ConnectivityStatus.ONLINE]: { tone: 'success', label: 'online' },
  [ConnectivityStatus.OFFLINE]: { tone: 'danger', label: 'offline' },
  [ConnectivityStatus.UNKNOWN]: { tone: 'neutral', label: 'unknown' },
};

/** Connectivity cell (ADR-0020); null when the asset is not monitored. */
export function getConnectivityBadge(
  status: ConnectivityStatus | null,
  lastSeenAt: string | null,
): ConnectivityBadge | null {
  if (!status) {
    return null;
  }
  return {
    ...BADGES[status],
    explainsUnknown: status === ConnectivityStatus.UNKNOWN,
    lastSeenAt,
  };
}

/** Last seen cell: "-" when not monitored, "Never seen" when monitored without a date. */
export function getLastSeenCell(
  status: ConnectivityStatus | null,
  lastSeenAt: string | null,
): LastSeenCell {
  if (!status) {
    return { kind: 'notMonitored' };
  }
  return lastSeenAt
    ? { kind: 'date', value: lastSeenAt }
    : { kind: 'neverSeen' };
}

const ASSET_LIST_REFETCH_INTERVAL_MS = 30_000;
const ASSET_LIST_MAX_RETRIES = 3;

/** Keeps connectivity fresh every 30 s, but stops polling once the session has expired (401). */
export function assetListRefetchInterval(error: Error | null): number | false {
  return isUnauthorizedError(error) ? false : ASSET_LIST_REFETCH_INTERVAL_MS;
}

/** Retries transient failures, never a 401 (a new request cannot succeed without a new login). */
export function shouldRetryAssetList(
  failureCount: number,
  error: Error,
): boolean {
  return !isUnauthorizedError(error) && failureCount < ASSET_LIST_MAX_RETRIES;
}
